import React, { useState, useEffect, useRef } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

const Gallery = ({ images = [], title = "" }) => {
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const stripRef = useRef(null);

  const list = images.filter(Boolean);

  const go = (delta) => {
    setActive((i) => (i + delta + Math.max(list.length, 1)) % Math.max(list.length, 1));
  };

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(false);
      if (e.key === "ArrowRight") setActive((i) => (i + 1) % Math.max(list.length, 1));
      if (e.key === "ArrowLeft") setActive((i) => (i - 1 + Math.max(list.length, 1)) % Math.max(list.length, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, list.length]);

  if (list.length === 0) return null;

  return (
    <div>
      {/* Main image */}
      <div className="relative aspect-[16/10] rounded-2xl overflow-hidden bg-black/5 shadow-[0_30px_60px_-30px_rgba(0,0,0,.3)]">
        <img
          src={list[active]}
          alt={`${title} - ${active + 1}`}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover cursor-zoom-in"
          onClick={() => setLightbox(true)}
        />
        {list.length > 1 && (
          <>
            <button
              onClick={() => go(-1)}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow"
              aria-label="Previous"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => go(1)}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/85 hover:bg-white flex items-center justify-center shadow"
              aria-label="Next"
            >
              <ChevronRight size={18} />
            </button>
            <div className="absolute bottom-3 right-3 bg-black/60 text-white text-[12px] px-2.5 py-1 rounded-full">
              {active + 1} / {list.length}
            </div>
          </>
        )}
      </div>

      {/* Thumbnail strip */}
      {list.length > 1 && (
        <div
          ref={stripRef}
          className="mt-3 flex gap-2 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin"
          style={{ scrollbarWidth: "thin" }}
        >
          {list.map((src, idx) => (
            <button
              key={idx}
              onClick={() => setActive(idx)}
              className={`relative shrink-0 w-24 h-16 sm:w-32 sm:h-20 rounded-lg overflow-hidden border-2 snap-start transition-all ${
                idx === active ? "border-[var(--ri-red)]" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img src={src} alt={`thumb-${idx}`} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightbox(false)}
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(false); }}
            className="absolute top-5 right-5 text-white p-2 rounded-full bg-white/10 hover:bg-white/20"
            aria-label="Close"
          >
            <X size={22} />
          </button>
          {list.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); go(-1); }}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                aria-label="Previous"
              >
                <ChevronLeft size={22} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); go(1); }}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center"
                aria-label="Next"
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}
          <img
            src={list[active]}
            alt={`${title} - full`}
            className="max-h-[90vh] max-w-[92vw] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          {list.length > 1 && (
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 bg-white/15 text-white text-[13px] px-3 py-1.5 rounded-full">
              {active + 1} / {list.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Gallery;
